import { useState, useEffect, useCallback } from 'react'
import { VideoService } from '../../services/videos'
import type { Video } from '../../services/videos'
import { ChatPromptService } from '../../services/chatPrompts'
import { AdminSettingsService } from '../../services/adminSettings'
import { SchoolService } from '../../services/schools'
import {
  AI_RESPONSE_POLICY_DEFAULT_INSTRUCTIONS,
  AI_RESPONSE_POLICY_SETTING_KEYS,
  normalizeAiResponsePolicy,
} from '../../constants/aiResponsePolicy'
import { AttachmentService, type VideoAttachment } from '../../services/attachments'
import { DEFAULT_VIDEO_INITIAL_MESSAGE } from '../../constants/chat'
import AttachmentManagementSection from './AttachmentManagementSection'
import VideoThumbnail from '../VideoThumbnail'

interface SchoolVideoManagerProps {
  schoolId: string
}

type VideoType = 'youtube' | 'chat_only'
type ThumbnailMode = 'image' | 'text'

interface FormData {
  title: string
  description: string
  youtubeInput: string
  videoType: VideoType
  systemPrompt: string
  initialMessage: string
  thumbnailTitle: string
  thumbnailMode: ThumbnailMode
}

const emptyForm: FormData = {
  title: '',
  description: '',
  youtubeInput: '',
  videoType: 'youtube',
  systemPrompt: '',
  initialMessage: DEFAULT_VIDEO_INITIAL_MESSAGE,
  thumbnailTitle: '',
  thumbnailMode: 'image',
}

const SchoolVideoManager = ({ schoolId }: SchoolVideoManagerProps) => {
  const [videos, setVideos] = useState<Video[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [isSaving, setIsSaving] = useState(false)
  const [error, setError] = useState('')
  const [saveMessage, setSaveMessage] = useState('')

  const [showForm, setShowForm] = useState(false)
  const [editingVideo, setEditingVideo] = useState<Video | null>(null)
  const [formData, setFormData] = useState<FormData>(emptyForm)
  const [showMiraiWork, setShowMiraiWork] = useState(false)

  // chat_only thumbnail
  const [thumbnailFile, setThumbnailFile] = useState<File | null>(null)
  const [thumbnailPreview, setThumbnailPreview] = useState<string | null>(null)

  // chat prompt tracking
  const [existingChatPromptId, setExistingChatPromptId] = useState<string | null>(null)

  // Starting prompt for a video that has none of its own: the instruction text of the
  // AI回答方針 this school selected in 学校設定. Leaving it unedited is the expected
  // case — the edge function detects that it matches the policy and sends it once.
  const [defaultSystemPrompt, setDefaultSystemPrompt] = useState('')

  // attachments for the video being edited
  const [attachments, setAttachments] = useState<VideoAttachment[]>([])
  const [attachmentError, setAttachmentError] = useState('')

  const loadVideos = useCallback(async () => {
    try {
      setIsLoading(true)
      setError('')
      const data = await VideoService.getSchoolVideos(schoolId)
      setVideos(data)

    } catch (err) {
      console.error('Failed to load school videos:', err)
      setError('動画の読み込みに失敗しました')
    } finally {
      setIsLoading(false)
    }
  }, [schoolId])

  useEffect(() => {
    loadVideos()
  }, [loadVideos])

  // Resolve this school's AI回答方針 into the instruction text the AI actually receives,
  // and use it as the starting prompt. Reading the admin-edited row needs the teacher
  // RLS policy from migration 102; without it getSettingValue yields the built-in text,
  // which is the same wording admins start from, so the form still works.
  useEffect(() => {
    let cancelled = false

    const loadDefaultPrompt = async () => {
      try {
        const school = await SchoolService.getSchool(schoolId)
        const policy = normalizeAiResponsePolicy(school?.ai_response_policy)
        const value = await AdminSettingsService.getSettingValue(
          AI_RESPONSE_POLICY_SETTING_KEYS[policy].instruction,
          AI_RESPONSE_POLICY_DEFAULT_INSTRUCTIONS[policy]
        )
        if (cancelled) return
        setDefaultSystemPrompt(
          (value ?? '').trim() || AI_RESPONSE_POLICY_DEFAULT_INSTRUCTIONS[policy]
        )
      } catch (err) {
        console.warn('Could not load the default system prompt:', err)
      }
    }

    loadDefaultPrompt()
    return () => { cancelled = true }
  }, [schoolId])

  const openAddForm = () => {
    setEditingVideo(null)
    setFormData({ ...emptyForm, systemPrompt: defaultSystemPrompt })
    setThumbnailFile(null)
    setThumbnailPreview(null)
    setExistingChatPromptId(null)
    setAttachments([])
    setShowMiraiWork(false)
    setError('')
    setSaveMessage('')
    setShowForm(true)
  }

  const openEditForm = async (video: Video) => {
    setEditingVideo(video)
    setThumbnailFile(null)
    setThumbnailPreview(null)
    setShowMiraiWork(false)
    setError('')
    setSaveMessage('')

    // load chat prompt if any
    const prompt = await ChatPromptService.getPromptByVideoId(video.id).catch(() => null)
    setExistingChatPromptId(prompt?.id ?? null)
    const hasMiraiWork = prompt !== null
    setShowMiraiWork(hasMiraiWork)

    setFormData({
      title: video.title,
      description: video.description ?? '',
      youtubeInput: video.youtube_id ?? '',
      videoType: video.video_type === 'chat_only' ? 'chat_only' : 'youtube',
      systemPrompt: prompt?.system_prompt?.trim() ? prompt.system_prompt : defaultSystemPrompt,
      initialMessage: prompt?.initial_message ?? DEFAULT_VIDEO_INITIAL_MESSAGE,
      thumbnailTitle: video.thumbnail_title ?? '',
      thumbnailMode: video.thumbnail_title && !video.thumbnail_url ? 'text' : 'image',
    })

    // load attachments
    const videoAttachments = await AttachmentService.getVideoAttachments(video.id).catch(() => [])
    setAttachments(videoAttachments)

    setShowForm(true)
  }

  const closeForm = () => {
    setShowForm(false)
    setEditingVideo(null)
    setFormData(emptyForm)
    setThumbnailFile(null)
    setThumbnailPreview(null)
    setExistingChatPromptId(null)
    setAttachments([])
    setShowMiraiWork(false)
    setError('')
  }

  const handleThumbnailChange = (file: File) => {
    setThumbnailFile(file)
    const reader = new FileReader()
    reader.onload = (e) => setThumbnailPreview(e.target?.result as string)
    reader.readAsDataURL(file)
  }

  const saveChatPrompt = async (videoId: string): Promise<string | null> => {
    // Always keep a per-video chat prompt row so every video has its own chat
    // (and its own conversation thread). An empty system prompt is allowed — the
    // chat falls back to the generic system prompt at request time.
    const promptPayload = {
      video_id: videoId,
      title: formData.title,
      system_prompt: formData.systemPrompt.trim(),
      initial_message: formData.initialMessage.trim() || null,
      is_active: true,
      // The school video form does not expose a model picker, so defer to the
      // system default (admin ai_default_model). Without this the column default
      // 'gpt-4' would be stored and pin these chats to gpt-4 at request time.
      model: 'default',
    }
    if (existingChatPromptId) {
      await ChatPromptService.updatePrompt(existingChatPromptId, promptPayload)
      return existingChatPromptId
    } else {
      const created = await ChatPromptService.createPrompt(promptPayload)
      return created.id
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setSaveMessage('')

    let youtubeId: string | null = null
    if (formData.videoType === 'youtube') {
      youtubeId = VideoService.extractYouTubeId(formData.youtubeInput)
      if (!youtubeId) {
        setError('有効なYouTube URLまたは動画IDを入力してください')
        return
      }
    }

    try {
      setIsSaving(true)

      if (editingVideo) {
        if (formData.videoType === 'chat_only') {
          await VideoService.updateDemoVideo(
            editingVideo.id,
            {
              title: formData.title,
              description: formData.description || null,
              thumbnail_title:
                formData.thumbnailMode === 'text' ? formData.thumbnailTitle.trim() || null : null,
            },
            formData.thumbnailMode === 'image' ? thumbnailFile ?? undefined : undefined
          )
        } else {
          await VideoService.updateSchoolVideo(editingVideo.id, {
            title: formData.title,
            description: formData.description || null,
            youtube_id: youtubeId!,
          })
        }
        await saveChatPrompt(editingVideo.id)
        setSaveMessage('動画を更新しました')
        await loadVideos()
        closeForm()
      } else {
        let newVideo: Video
        if (formData.videoType === 'chat_only') {
          newVideo = await VideoService.createSchoolChatOnlyVideo(
            {
              title: formData.title,
              description: formData.description || null,
              school_id: schoolId,
              thumbnail_title:
                formData.thumbnailMode === 'text' ? formData.thumbnailTitle.trim() || null : null,
            },
            formData.thumbnailMode === 'image' ? thumbnailFile ?? undefined : undefined
          )
        } else {
          newVideo = await VideoService.createSchoolVideo({
            title: formData.title,
            description: formData.description || null,
            youtube_id: youtubeId!,
            school_id: schoolId,
          })
        }
        const promptId = await saveChatPrompt(newVideo.id)
        await loadVideos()
        // Transition to edit mode so user can add attachments without reopening the form
        setEditingVideo(newVideo)
        setExistingChatPromptId(promptId)
        setThumbnailFile(null)
        setThumbnailPreview(null)
        setSaveMessage('コンテンツを追加しました。添付ファイルを追加できます。')
      }
    } catch (err) {
      console.error('Failed to save school video:', err)
      setError(err instanceof Error ? err.message : '保存に失敗しました')
    } finally {
      setIsSaving(false)
    }
  }

  const handleDelete = async (video: Video) => {
    if (!confirm(`「${video.title}」を削除しますか？`)) return
    try {
      // delete associated chat prompt first
      const prompt = await ChatPromptService.getPromptByVideoId(video.id).catch(() => null)
      if (prompt) await ChatPromptService.deletePrompt(prompt.id).catch(() => {})
      await VideoService.deleteSchoolVideo(video.id)
      setSaveMessage('動画を削除しました')
      setTimeout(() => setSaveMessage(''), 3000)
      await loadVideos()
    } catch (err) {
      console.error('Failed to delete school video:', err)
      setError(err instanceof Error ? err.message : '削除に失敗しました')
    }
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-bold text-gray-900">コンテンツ管理</h2>
        <button
          onClick={openAddForm}
          className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-medium text-sm"
        >
          コンテンツを追加
        </button>
      </div>

      {/* Messages */}
      {saveMessage && (
        <div className="bg-green-50 border border-green-200 rounded-md p-3 text-green-800 text-sm">
          {saveMessage}
        </div>
      )}
      {error && !showForm && (
        <div className="bg-red-50 border border-red-200 rounded-md p-3 text-red-800 text-sm">
          {error}
        </div>
      )}

      {/* Add/Edit Form */}
      {showForm && (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-5">
          <h3 className="text-base font-semibold text-gray-900 mb-4">
            {editingVideo ? 'コンテンツを編集' : 'コンテンツを追加'}
          </h3>
          {error && (
            <div className="mb-4 bg-red-50 border border-red-200 rounded-md p-3 text-red-800 text-sm">
              {error}
            </div>
          )}
          <form onSubmit={handleSubmit} className="space-y-4">

            {/* Video type — only on new video */}
            {!editingVideo && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">コンテンツタイプ</label>
                <div className="flex gap-3">
                  {(['youtube', 'chat_only'] as VideoType[]).map((type) => (
                    <button
                      key={type}
                      type="button"
                      onClick={() => setFormData({ ...formData, videoType: type })}
                      className={`px-4 py-2 rounded-lg text-sm font-medium border transition-colors ${
                        formData.videoType === type
                          ? 'bg-blue-600 text-white border-blue-600'
                          : 'bg-white text-gray-700 border-gray-300 hover:border-blue-400'
                      }`}
                    >
                      {type === 'youtube' ? 'YouTube動画' : 'チャットのみ'}
                    </button>
                  ))}
                </div>
                {formData.videoType === 'chat_only' && (
                  <p className="mt-1 text-xs text-gray-500">
                    動画なし。チャット画面のみ表示されます。
                  </p>
                )}
              </div>
            )}

            {/* Title */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                タイトル <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                required
              />
            </div>

            {/* YouTube URL — only for youtube type */}
            {formData.videoType === 'youtube' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  YouTube URL または 動画ID <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.youtubeInput}
                  onChange={(e) => setFormData({ ...formData, youtubeInput: e.target.value })}
                  placeholder="https://www.youtube.com/watch?v=... または 動画ID"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                  required
                />
              </div>
            )}

            {/* Thumbnail — required for chat_only, optional for youtube (auto-generated) */}
            {formData.videoType === 'chat_only' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">サムネイルの表示方法</label>
                <div className="flex items-center gap-6 mb-3">
                  <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                    <input
                      type="radio"
                      name="thumbnailMode"
                      checked={formData.thumbnailMode === 'image'}
                      onChange={() => setFormData({ ...formData, thumbnailMode: 'image' })}
                    />
                    画像を使う
                  </label>
                  <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                    <input
                      type="radio"
                      name="thumbnailMode"
                      checked={formData.thumbnailMode === 'text'}
                      onChange={() => setFormData({ ...formData, thumbnailMode: 'text' })}
                    />
                    文字を使う
                  </label>
                </div>

                {formData.thumbnailMode === 'image' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      サムネイル画像{' '}
                      {!editingVideo && <span className="text-gray-400 font-normal">（任意）</span>}
                      {editingVideo && <span className="text-gray-400 font-normal">（変更する場合のみ）</span>}
                    </label>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => {
                        const file = e.target.files?.[0]
                        if (file) handleThumbnailChange(file)
                      }}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                    />
                    {thumbnailPreview && (
                      <img src={thumbnailPreview} alt="プレビュー" className="mt-2 h-24 w-auto rounded object-cover" />
                    )}
                    {!thumbnailPreview && editingVideo?.thumbnail_url && (
                      <img src={editingVideo.thumbnail_url} alt="現在のサムネイル" className="mt-2 h-24 w-auto rounded object-cover opacity-60" />
                    )}
                  </div>
                )}

                {formData.thumbnailMode === 'text' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">サムネイル文字</label>
                    <textarea
                      value={formData.thumbnailTitle}
                      onChange={(e) => setFormData({ ...formData, thumbnailTitle: e.target.value })}
                      rows={2}
                      placeholder={'探究コンテストに\n挑戦しよう'}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                    />
                    <p className="mt-1 text-xs text-gray-500">
                      改行で行を分けられます（2行まで／各行8文字・合計16文字が目安）。空欄の場合はタイトルを表示します。
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Description */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">説明（任意）</label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows={3}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
              />
            </div>

            {/* みらいワーク section */}
            <div className="border border-gray-200 rounded-lg overflow-hidden">
              <button
                type="button"
                onClick={() => setShowMiraiWork(!showMiraiWork)}
                className="w-full flex items-center justify-between px-4 py-3 bg-white hover:bg-gray-50 text-sm font-medium text-gray-700"
              >
                <span>みらいワーク設定（AI チャット）</span>
                <span className="text-gray-400">{showMiraiWork ? '▲' : '▼'}</span>
              </button>
              {showMiraiWork && (
                <div className="px-4 pb-4 pt-2 bg-white border-t border-gray-100 space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      システムプロンプト
                    </label>
                    <p className="text-xs text-gray-500 mb-1">
                      学校設定で選んだAIの回答方針が入力されています。通常はこのまま利用できます。
                      <br />
                      必要に応じて、授業の内容に合わせて編集してください。編集した内容が優先され、
                      書かれていない点については学校の回答方針が使われます。
                    </p>
                    <textarea
                      value={formData.systemPrompt}
                      onChange={(e) => setFormData({ ...formData, systemPrompt: e.target.value })}
                      rows={4}
                      placeholder="AIへの指示を入力してください（例：この動画について生徒と対話してください）"
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                    />
                    <button
                      type="button"
                      onClick={() => setFormData({ ...formData, systemPrompt: defaultSystemPrompt })}
                      className="mt-1 text-xs text-blue-600 hover:text-blue-800 font-medium"
                    >
                      回答方針の初期文に戻す
                    </button>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      最初のメッセージ
                    </label>
                    <textarea
                      value={formData.initialMessage}
                      onChange={(e) => setFormData({ ...formData, initialMessage: e.target.value })}
                      rows={3}
                      placeholder="チャット開始時にAIが送るメッセージ"
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                    />
                    <button
                      type="button"
                      onClick={() => setFormData({ ...formData, initialMessage: DEFAULT_VIDEO_INITIAL_MESSAGE })}
                      className="mt-1 text-xs text-blue-600 hover:text-blue-800 font-medium"
                    >
                      標準メッセージに戻す
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Attachments — edit mode only */}
            {editingVideo && (
              <AttachmentManagementSection
                video={editingVideo}
                attachments={attachments}
                onAttachmentsUpdate={async () => {
                  const updated = await AttachmentService.getVideoAttachments(editingVideo.id).catch(() => [])
                  setAttachments(updated)
                }}
                onError={setAttachmentError}
              />
            )}
            {attachmentError && (
              <div className="bg-red-50 border border-red-200 rounded-md p-2 text-red-700 text-xs">
                {attachmentError}
              </div>
            )}

            <div className="flex gap-3 pt-2">
              <button
                type="submit"
                disabled={isSaving}
                className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium disabled:opacity-50"
              >
                {isSaving ? '保存中...' : (editingVideo ? '更新' : '追加')}
              </button>
              <button
                type="button"
                onClick={closeForm}
                className="bg-gray-200 hover:bg-gray-300 text-gray-700 px-4 py-2 rounded-lg text-sm font-medium"
              >
                キャンセル
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Video List */}
      {isLoading ? (
        <div className="flex justify-center items-center h-32">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
        </div>
      ) : videos.length === 0 ? (
        <div className="text-center py-12 text-gray-500 text-sm">
          まだコンテンツがありません。「コンテンツを追加」から追加してください。
        </div>
      ) : (
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">サムネイル</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">タイトル</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">タイプ</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">操作</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {videos.map((video) => (
                <tr key={video.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3">
                    <VideoThumbnail
                      thumbnailUrl={video.thumbnail_url}
                      title={video.title}
                      thumbnailTitle={video.thumbnail_title}
                      schoolOriginal={video.video_type === 'chat_only'}
                      className="w-20 h-12 rounded overflow-hidden"
                    />
                  </td>
                  <td className="px-4 py-3">
                    <div className="text-sm font-medium text-gray-900">{video.title}</div>
                    {video.description && (
                      <div className="text-xs text-gray-500 mt-0.5 line-clamp-1">{video.description}</div>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    {video.video_type === 'chat_only' ? (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                        チャットのみ
                      </span>
                    ) : (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        YouTube
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-sm space-x-3">
                    <button
                      onClick={() => openEditForm(video)}
                      className="text-blue-600 hover:text-blue-800 font-medium"
                    >
                      編集
                    </button>
                    <button
                      onClick={() => handleDelete(video)}
                      className="text-red-600 hover:text-red-800 font-medium"
                    >
                      削除
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

export default SchoolVideoManager
